import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useAsyncEffect from 'use-async-effect';

import { useMytaverseEvent } from '../../../providers';
import MediaDeviceService from './MediaDeviceService';
import { getNextSelectedCamera } from './helpers';

import { IMediaStream, IParticipant } from '@interfaces/participants';
import { ParticipantPosition } from '../interfaces';
import { IConferenceContext, IDolbyMediaStream } from './interfaces';
import {
  ILastWebsocketJsonMessage,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';
import { useConferenceState } from '@hooks/conferenceContext';
import { NavigatorPermissionsService } from '@helpers/permissionsService';
import { IWebsocketMessageFlag } from '../../../providers/MytaverseEventProvider/interfaces';

const { SyncDataWeb } = WebsocketAction;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const ConferenceContext = React.createContext<IConferenceContext>({});

export const useConference = () => useContext(ConferenceContext);

type ConferenceProviderProps = {
  children: ReactNode;
};

export const ConferenceProvider: React.FC<ConferenceProviderProps> = ({
  children,
}) => {
  const [previewingParticipant, setPreviewingParticipant] =
    React.useState<IParticipant | null>(null);

  const prevSyncDataWebPayload = useRef('');
  const [isFirstPersonView, setIsFirstPersonView] =
    React.useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [videoStarted, setVideoStarted] = React.useState<boolean>(false);
  const [microphones, setMicrophones] = React.useState<any[]>([]);
  const [speakers, setSpeakers] = React.useState<any[]>([]);
  const [cameras, setCameras] = React.useState<any[]>([]);
  const [disabledPersonView, setDisabledPersonView] = React.useState(false);

  const [ueScreenMediaStreams, setUEScreenMediaStreams] = React.useState<
    IDolbyMediaStream[]
  >([]);
  const [ueWebcamMediaStreams, setUEWebcamMediaStreams] = React.useState<
    IDolbyMediaStream[]
  >([]);

  const shareWebcamToUEMediaStream = React.useRef<IMediaStream | null>(null);

  const [ueWebcamScreenName, setUEWebcamScreenName] = useState<string | null>(
    null,
  );

  const [ueScreenScreenName, setUEScreenScreenName] = useState<string | null>(
    null,
  );

  const [screenMediaStreams, setScreenMediaStreams] = useState<IMediaStream[]>(
    [],
  );

  const [lastParticipantPosition, setLastParticipantPosition] =
    React.useState<ParticipantPosition | null>(null);

  const {
    setActiveCameraDeviceId,
    setActiveMicroDeviceId,
    activeMicroDeviceId,
  } = useConferenceState();

  const {
    muted,
    currentRoom,
    currentRegion,
    currentEvent,
    currentParticipant,
  } = useMytaverseEvent();

  // UE5 does not send a position update immediately after teleport — reset
  // stale screen targets so the camera menu reflects the new room instantly.
  useEffect(() => {
    setUEWebcamScreenName(null);
    setUEScreenScreenName(null);
  }, [currentRoom?.roomId]);

  useAsyncEffect(async () => {
    const initDevices = async () => {
      const audioOutput = await MediaDeviceService.enumerateAudioOutputs();
      const audioInput = await MediaDeviceService.enumerateAudioInputs();
      const videoInput = await MediaDeviceService.enumerateVideoInputs();

      setMicrophones(audioInput);
      setSpeakers(audioOutput);
      setCameras(videoInput);
    };

    initDevices();

    navigator.mediaDevices?.addEventListener('devicechange', initDevices);

    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', initDevices);
    };
  }, []);

  const setParticipantPosition = React.useCallback(
    (participantPosition: ParticipantPosition) => {
      // setLastParticipantPosition((prev) => {
      //   if (!prev) {
      //     return participantPosition;
      //   }
      //
      //   if (
      //     prev.timestamp !== 0 &&
      //     participantPosition.timestamp < prev.timestamp
      //   ) {
      //     return prev;
      //   }
      //
      //   if (
      //     prev.x !== participantPosition.x ||
      //     prev.y !== participantPosition.y ||
      //     prev.z !== participantPosition.z ||
      //     prev.r !== participantPosition.r
      //   ) {
      //     return participantPosition;
      //   }
      //
      //   return prev;
      // });
    },
    [],
  );

  useAsyncEffect(async () => {
    const permissionData = await NavigatorPermissionsService.checkPermission(
      'microphone',
    );

    if (!permissionData) {
      return;
    }

    navigator.permissions
      .query({
        name: 'microphone' as any,
      })
      .then((permissionStatus) => {
        permissionStatus.onchange = (async (action: any) => {
          if (action.currentTarget.state === 'granted') {
            const audioOutput =
              await MediaDeviceService.enumerateAudioOutputs();
            const audioInput = await MediaDeviceService.enumerateAudioInputs();

            setMicrophones(audioInput);
            setSpeakers(audioOutput);
          }
        }) as any;
      });
    navigator.permissions
      .query({
        name: 'camera' as any,
      })
      .then(async (permissionStatus) => {
        permissionStatus.onchange = async (action: any) => {
          if (action.currentTarget.state === 'granted') {
            const videoInput = await MediaDeviceService.enumerateVideoInputs();

            setCameras(videoInput);

            const nextSelectedCamera = getNextSelectedCamera(videoInput);

            setActiveCameraDeviceId(nextSelectedCamera);
          }
        };
      });
  }, [currentEvent, currentRoom, currentRegion, currentParticipant]);

  const selectMicrophoneHandle = useCallback(async (microphoneId: string) => {
    if (microphoneId) {
      setActiveMicroDeviceId(microphoneId);
    }
  }, []);

  const selectCameraHandle = useCallback(async (cameraId: string) => {
    if (cameraId) {
      setActiveCameraDeviceId(cameraId);
    }
  }, []);

  const handleDolbyWSMessages = React.useCallback(
    async (lastJsonMessage: ILastWebsocketJsonMessage) => {
      const { action } = lastJsonMessage;

      if (!action) {
        return;
      }

      switch (action) {
        case SyncDataWeb: {
          const { timestamp: _lastMessageTimestamp, ...syncPayloadRest } =
            lastJsonMessage;

          if (
            JSON.stringify(syncPayloadRest) === prevSyncDataWebPayload.current
          ) {
            return;
          }

          prevSyncDataWebPayload.current = JSON.stringify(syncPayloadRest);

          const syncPayload = lastJsonMessage as ILastWebsocketJsonMessage & {
            videoScreenStreams?: IDolbyMediaStream[];
            videoWebcamStreams?: IDolbyMediaStream[];
          };
          const screenStreams =
            syncPayload.videoScreenStreams ?? syncPayload.zoomScreenStreams;
          const webcamStreams =
            syncPayload.videoWebcamStreams ?? syncPayload.zoomWebcamStreams;

          if (screenStreams) {
            setUEScreenMediaStreams((prev) => {
              if (prev.length !== screenStreams.length) {
                return screenStreams;
              }

              const mediaStreamIds = screenStreams.map(
                (dms) => dms.mediaStreamId,
              );

              if (
                !prev.every((dms) => mediaStreamIds.includes(dms.mediaStreamId))
              ) {
                return screenStreams;
              }

              return prev;
            });
          }
          if (webcamStreams) {
            setUEWebcamMediaStreams((prev) => {
              if (prev.length !== webcamStreams.length) {
                return webcamStreams;
              }

              const mediaStreamIds = webcamStreams.map(
                (dms) => dms.mediaStreamId,
              );

              if (
                !prev.every((dms) => mediaStreamIds.includes(dms.mediaStreamId))
              ) {
                return webcamStreams;
              }

              return prev;
            });
          }
          break;
        }
        default:
          break;
      }
    },
    [],
  );

  return (
    <ConferenceContext.Provider
      value={{
        isFirstPersonView,
        previewingParticipant,
        muted,
        videoStarted,
        microphones,
        speakers,
        cameras,
        disabledPersonView,
        setScreenMediaStreams,
        setDisabledPersonView,
        setPreviewingParticipant,
        setIsFirstPersonView,
        selectMicrophone: selectMicrophoneHandle,
        selectCamera: selectCameraHandle,
        setParticipantPosition,
        activeMicroDeviceId,
        screenMediaStreams,

        setVideoStarted,
        ueWebcamScreenName,
        setUEWebcamScreenName,
        ueScreenScreenName,
        setUEScreenScreenName,
        shareWebcamToUEMediaStream,
        handleDolbyWSMessages,
        ueWebcamMediaStreams,
        ueScreenMediaStreams,
      }}
    >
      {children}
    </ConferenceContext.Provider>
  );
};
